import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleModal from 'components/SimpleModal';
import ApiClientManager from 'core/apiClient';
import { useNotification } from 'hooks/useNotification';
import { selectAuthToken } from 'selectors';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';
import { CHELLO_TIERS, ChelloTier, setChelloTierApi } from 'TeamModule/api/setChelloTierApi';
import type { ActionMenuItem } from 'components/ActionMenu';

export interface OpenSetChelloTierModalArgs {
  /** Tenant name for API context - falls back to the global tenant when omitted */
  tenant?: string;
  /** Realm ID for API context - falls back to the global realm when omitted */
  realmId?: string;
  teamId: number;
  /** Mosaic account ids of the selected members */
  accountIds: number[];
  tier: ChelloTier;
}

/**
 * The "Chello Subscription Tier" entry of a members bulk menu: one sub-item per
 * offered tier. Leaf items are disabled (and inert) when the selection cannot
 * be acted on, mirroring how "Modify Access Level" gates its role options.
 */
export const buildChelloTierMenuItem = ({
  enabled,
  onSelect,
}: {
  enabled: boolean;
  onSelect: (tier: ChelloTier) => void;
}): ActionMenuItem => ({
  label: 'Chello Subscription Tier',
  subItems: CHELLO_TIERS.map((tier) => ({
    label: tier,
    disabled: !enabled,
    onClick: enabled ? () => onSelect(tier) : undefined,
  })),
});

/**
 * Confirm-then-apply modal behind the "Chello Subscription Tier" bulk action
 * (GLX-1945). Shared by the Members page and the team-profile members table so
 * both menus send the same request and report the same way.
 */
export const useSetChelloTierModal = () => {
  const { openModal } = useDynamicModal();
  const token = useSelector(selectAuthToken);
  const { showSuccess, showError } = useNotification();

  const openSetChelloTierModal = useCallback(
    ({ tenant, realmId, teamId, accountIds, tier }: OpenSetChelloTierModalArgs) => {
      const onConfirm = async () => {
        if (!token) return;
        try {
          const { data } = await setChelloTierApi({
            params: { teamId, memberIds: accountIds, tier },
            token,
            config: { headers: ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId }) },
          });
          showSuccess(
            `Chello tier set to ${tier}: ${data.updated} updated, ${data.created} created, ${data.unchanged} already on ${tier}.`,
          );
        } catch (e) {
          showError(`Chello tier update failed: ${extractApiErrorMessage(e)}`);
        }
      };

      openModal({
        modal: ({ toggle }) => (
          <SimpleModal
            open
            onClose={toggle}
            onConfirm={onConfirm}
            closeOnConfirm
            headerText="Chello Subscription Tier"
            Body={
              <div>
                Set the Chello subscription tier to <strong>{tier}</strong> for {accountIds.length}{' '}
                member{accountIds.length === 1 ? '' : 's'}? This applies to the current billing
                month.
              </div>
            }
          />
        ),
      });
    },
    [openModal, showError, showSuccess, token],
  );

  return { openSetChelloTierModal };
};
